/**
 * Stock, construction and appearance profiles (test-plan §1), their Zod schemas,
 * and the bundled defaults loaded from `profiles/`.
 *
 * The maker's measured thickness and profile review are recorded in `profiles/`,
 * never in the customer UI. While `provisional` is true every pack is watermarked.
 */
import { z } from 'zod';
import stockJson from '../../profiles/stock/birch-ply-18.json' with { type: 'json' };
/** The grid family's self-authored joint profile is the default construction profile (plan §4.6, §8.2). */
import constructionJson from '../families/grid-bookcase/joints.json' with { type: 'json' };
import appearanceJson from '../../profiles/appearance/default.json' with { type: 'json' };

const ticks = z.int().positive();
const minMax = z.object({ min: z.int(), max: z.int() }).refine((r) => r.min <= r.max, 'min must not exceed max');

export const StockProfileSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().min(1),
  version: z.string().min(1),
  material: z.string().min(1).default('Birch plywood'),
  nominalDescription: z.string().min(1),
  backDescription: z.string().min(1).optional(),
  nominalThicknessTicks: ticks.optional(),
  measuredThicknessTicks: ticks,
  nominalBackThicknessTicks: ticks.optional(),
  backThicknessTicks: ticks,
  measuredBy: z.string().nullable().optional(),
  measuredAt: z.string().nullable().optional(),
  plies: z.object({ carcass: z.int().positive(), back: z.int().positive() }),
  sheet: z.object({ lengthTicks: ticks, widthTicks: ticks }),
  grainAxis: z.enum(['length', 'width']),
  allowRotation: z.boolean(),
  faceGrade: z.string().min(1),
  finishSpec: z.string().min(1),
  notes: z.string().optional(),
});
export type StockProfile = z.infer<typeof StockProfileSchema>;

const FastenerSchema = z.discriminatedUnion('qtyRule', [
  z.object({ id: z.string(), spec: z.string(), qtyRule: z.literal('per-joint'), perJoint: z.int().positive(), use: z.string() }),
  z.object({ id: z.string(), spec: z.string(), qtyRule: z.literal('back-perimeter-and-shelves'), centresMm: z.number().positive(), use: z.string() }),
  z.object({ id: z.string(), spec: z.string(), qtyRule: z.literal('fixed'), qty: z.int().nonnegative(), use: z.string() }),
]);
export type Fastener = z.infer<typeof FastenerSchema>;

export const ConstructionProfileSchema = z
  .object({
    schemaVersion: z.literal(1),
    id: z.string().min(1),
    version: z.string().min(1),
    name: z.string().min(1),
    family: z.string().min(1),
    jointFamily: z.enum(['butt', 'housed', 'mixed']),
    provisional: z.boolean(),
    reviewer: z.string().min(1).nullable(),
    reviewedAt: z.string().min(1).nullable(),
    joint: z.string().min(1),
    /** W, H, D in ticks; n is a count. */
    envelope: z.object({ W: minMax, H: minMax, D: minMax, n: minMax, minOpeningTicks: ticks }),
    edgeAllowanceTicks: z.int().nonnegative(),
    cutAllowanceTicks: z.object({ l: z.int().nonnegative(), w: z.int().nonnegative() }).default({ l: 0, w: 0 }),
    arrisMm: z.number().nonnegative(),
    intendedUse: z.string().min(1),
    loadStatement: z.string().min(1),
    edgeTreatment: z.string().min(1),
    fasteners: z.array(FastenerSchema).min(1),
    backFixing: z.object({ method: z.string().min(1), spacingMm: z.number().positive(), rackingNote: z.string().min(1) }),
    wallFixing: z.object({ required: z.boolean(), method: z.string().min(1), hardware: z.string().min(1) }),
    antiTip: z.object({ required: z.boolean(), method: z.string().min(1) }),
    warnings: z.object({ maxShelfSpanMm: z.number().positive(), antiTipAboveMm: z.number().positive() }),
    assemblySteps: z.array(z.string().min(1)).min(1),
    markingDrilling: z.array(z.string().min(1)),
    installation: z.array(z.string().min(1)),
    /** panelLength, panelWidth and shelfDatum in millimetres. */
    tolerances: z.object({
      panelLength: z.number().nonnegative(),
      panelWidth: z.number().nonnegative().optional(),
      squareness: z.string().min(1),
      shelfDatum: z.number().nonnegative(),
    }),
    backConstraint: z.string().optional(),
    // --- JointProfile (test-plan §1a): the families programme's per-family joints.json -----
    familyId: z.string().min(1).optional(),
    /** Which joint at which meeting ("shelf-side"), with its parameters. */
    joints: z.array(z.object({
      at: z.string().min(1),
      kind: z.enum(['housed-dado', 'dowel-butt', 'screwed-butt', 'rebate', 'pocket-screw', 'egg-crate']),
      params: z.object({ depthRatio: z.number().optional(), dowelsPer300: z.number().optional(), screwSpec: z.string().optional() }).optional(),
      note: z.string().optional(),
    })).optional(),
    limits: z.object({ maxSpanMm: z.number().positive(), maxCantileverRatio: z.number().positive().optional(), minOpeningMm: z.number().positive() }).optional(),
    /** Multi-bay envelope: bays, the widest overall piece and the narrowest bay (ticks). */
    bays: z.object({ min: z.int().min(1), max: z.int().min(1), maxOverallWTicks: ticks, minBayClearTicks: ticks, maxPlinthTicks: ticks }).optional(),
    selfAuthored: z.boolean().optional(),
    author: z.string().min(1).optional(),
    authoredAt: z.string().min(1).optional(),
    sources: z.array(z.string()).optional(),
  })
  .superRefine((p, ctx) => {
    if (!p.provisional && (!p.reviewer || !p.reviewedAt)) {
      ctx.addIssue({ code: 'custom', path: ['reviewer'], message: 'A reviewed (non-provisional) profile needs a reviewer and a review date.' });
    }
  });
export type ConstructionProfile = z.infer<typeof ConstructionProfileSchema>;

export const AppearanceProfileSchema = z.object({
  schemaVersion: z.literal(1),
  id: z.string().min(1),
  version: z.string().min(1),
  faceMaterialId: z.string().min(1),
  edgeMaterialId: z.string().min(1),
  hardwareAssetIds: z.array(z.string()),
  textureScaleMetres: z.number().positive(),
  bevelRadiusMm: z.number().nonnegative(),
  lightingPresetId: z.string().min(1),
  notes: z.string().optional(),
});
export type AppearanceProfile = z.infer<typeof AppearanceProfileSchema>;

export interface Profiles {
  stock: StockProfile;
  construction: ConstructionProfile;
  appearance: AppearanceProfile;
}

/** Envelope limits in the prototype's shape (ticks), used by validation and the UI hints. */
export interface Limits {
  W: { min: number; max: number; label: string };
  H: { min: number; max: number; label: string };
  D: { min: number; max: number; label: string };
  n: { min: number; max: number; label: string };
  minOpening: number;
  maxShelfSpanWarning: number;
  antiTipAbove: number;
}

export function limitsOf(c: ConstructionProfile): Limits {
  const e = c.envelope;
  return {
    W: { ...e.W, label: 'Width' },
    H: { ...e.H, label: 'Height' },
    D: { ...e.D, label: 'Depth' },
    n: { ...e.n, label: 'Shelves' },
    minOpening: e.minOpeningTicks,
    maxShelfSpanWarning: Math.round(c.warnings.maxShelfSpanMm * 10),
    antiTipAbove: Math.round(c.warnings.antiTipAboveMm * 10),
  };
}

/** True while the profile has not been reviewed by a maker (drives the pack watermark). */
export function isProvisional(c: ConstructionProfile): boolean {
  return !!(c.provisional || !c.reviewer);
}

/** A family's construction rules written by the author, not reviewed by a maker (plan §2, §5). */
export function isSelfAuthored(c: ConstructionProfile): boolean {
  return !!c.selfAuthored && isProvisional(c);
}

/** Display labels derived from a stock profile. */
export function stockLabels(s: StockProfile): { label: string; backLabel: string; nominalThicknessTicks: number; nominalBackThicknessTicks: number } {
  const nominalThicknessTicks = s.nominalThicknessTicks ?? s.measuredThicknessTicks;
  const nominalBackThicknessTicks = s.nominalBackThicknessTicks ?? s.backThicknessTicks;
  return {
    label: s.nominalDescription,
    backLabel: s.backDescription ?? `${s.material} ${nominalBackThicknessTicks / 10} mm, ${s.faceGrade}, ${s.finishSpec.toLowerCase()}`,
    nominalThicknessTicks,
    nominalBackThicknessTicks,
  };
}

export type ParseOutcome<T> = { ok: true; value: T } | { ok: false; errors: string[] };

function outcome<T>(r: { success: true; data: T } | { success: false; error: z.ZodError }): ParseOutcome<T> {
  if (r.success) return { ok: true, value: r.data };
  return { ok: false, errors: r.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`) };
}

export const parseStockProfile = (json: unknown): ParseOutcome<StockProfile> => outcome(StockProfileSchema.safeParse(json));
export const parseConstructionProfile = (json: unknown): ParseOutcome<ConstructionProfile> => outcome(ConstructionProfileSchema.safeParse(json));
export const parseAppearanceProfile = (json: unknown): ParseOutcome<AppearanceProfile> => outcome(AppearanceProfileSchema.safeParse(json));

/** Validate a set of profile JSON objects (e.g. read from disk by the service). */
export function loadProfiles(json: { stock: unknown; construction: unknown; appearance: unknown }): ParseOutcome<Profiles> {
  const s = parseStockProfile(json.stock);
  const c = parseConstructionProfile(json.construction);
  const a = parseAppearanceProfile(json.appearance);
  const errors = [
    ...(s.ok ? [] : s.errors.map((e) => `stock ${e}`)),
    ...(c.ok ? [] : c.errors.map((e) => `construction ${e}`)),
    ...(a.ok ? [] : a.errors.map((e) => `appearance ${e}`)),
  ];
  if (!s.ok || !c.ok || !a.ok) return { ok: false, errors };
  return { ok: true, value: { stock: s.value, construction: c.value, appearance: a.value } };
}

function deepFreeze<T>(o: T): T {
  if (o && typeof o === 'object' && !Object.isFrozen(o)) {
    Object.freeze(o);
    for (const v of Object.values(o as Record<string, unknown>)) deepFreeze(v);
  }
  return o;
}

function mustLoad(): Profiles {
  const r = loadProfiles({ stock: stockJson, construction: constructionJson, appearance: appearanceJson });
  if (!r.ok) throw new Error(`Bundled profiles in profiles/ are invalid:\n${r.errors.join('\n')}`);
  return deepFreeze(r.value);
}

/** The bundled default profiles (validated at module load). */
export const PROFILES: Readonly<Profiles> = mustLoad();
export const STOCK: StockProfile = PROFILES.stock;
export const CONSTRUCTION: ConstructionProfile = PROFILES.construction;
export const APPEARANCE: AppearanceProfile = PROFILES.appearance;
/** Envelope limits of the default construction profile (ticks). */
export const LIMITS: Limits = deepFreeze(limitsOf(CONSTRUCTION));
